import { UserCapabilityOption } from "./UserCapabilityOption";
import { UserCapability } from "./UserCapability";

/**
 * Interface defining the mapping of user capabilities to their options.
 */
type CapabilityMap = { [key in keyof typeof UserCapability]: UserCapabilityOption };

/**
 * Interface defining the configuration for a user role.
 */
export interface RoleConfiguration {
  label: string | null;
  description: string | null;
  roleActive: boolean;
  capabilityMap: CapabilityMap;
}

/**
 * Function to create a default role configuration.
 * @param {Object} data - Optional data to customize the default configuration.
 * @param {boolean} data.roleActive - Flag indicating if the role is active.
 * @param {CapabilityMap} data.capabilityMap - Mapping of capabilities to options.
 * @returns {RoleConfiguration} - The default role configuration.
 */
export function createDefaultRoleConfiguration(data?: {
  roleActive?: boolean;
  capabilityMap?: CapabilityMap;
}): RoleConfiguration {
  const { roleActive, capabilityMap } = data ?? {};

  return {
    label: null,
    description: null,
    roleActive: roleActive ?? true,

    capabilityMap: capabilityMap ?? {
      [UserCapability.EDIT_ADMIN]: UserCapabilityOption.NO,
      [UserCapability.EDIT_USER]: UserCapabilityOption.NO,
      [UserCapability.EDIT_VALIDATION]: UserCapabilityOption.NO,
      [UserCapability.SEE_ALL_SETTINGS]: UserCapabilityOption.NO,
      
      [UserCapability.BASIC_LICENCE]: UserCapabilityOption.NO,
      [UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION]:
        UserCapabilityOption.NO,
      [UserCapability.DRYING_AND_REPAIR]: UserCapabilityOption.NO,
      [UserCapability.OFFICE]: UserCapabilityOption.NO,
      [UserCapability.PROJECT_MANAGEMENT]: UserCapabilityOption.NO,
      
      [UserCapability.SEE_ALL_PROJECTS]: UserCapabilityOption.NO,
      [UserCapability.CREATE_PROJECT]: UserCapabilityOption.NO,
      [UserCapability.DELETE_PROJECT]: UserCapabilityOption.NO,
      [UserCapability.REOPEN_COMPLETED_PHASES]: UserCapabilityOption.NO,
      [UserCapability.EDIT_PROJECT]: UserCapabilityOption.NO,
      
      [UserCapability.ASSIGN_PROJECT_TO_SELF]: UserCapabilityOption.NO,
      [UserCapability.ASSIGN_PROJECT_TO_OTHERS]: UserCapabilityOption.NO,
      [UserCapability.PROJECT_SUPERVISION]: UserCapabilityOption.NO,
      
      [UserCapability.EDIT_DEVICE_SETTINGS]: UserCapabilityOption.NO,
      [UserCapability.IMPORT_DEVICES]: UserCapabilityOption.NO,
      [UserCapability.EDIT_DEVICES]: UserCapabilityOption.NO,
      
      [UserCapability.LEAK_DETECTION]: UserCapabilityOption.NO,
      [UserCapability.DAMAGE_ASSESSMENT_LARGE]: UserCapabilityOption.NO,
      [UserCapability.DRYING]: UserCapabilityOption.NO,
      [UserCapability.REPAIR]: UserCapabilityOption.NO,
      [UserCapability.HAS_VEHICLE]: UserCapabilityOption.NO,

      [UserCapability.CLIENT_ADMINISTRATION]: UserCapabilityOption.NO,
      [UserCapability.DEVICE_ADMINISTRATION]: UserCapabilityOption.NO,
      [UserCapability.SETTINGS_ADMINISTRATION]: UserCapabilityOption.NO,

      // Role settings stored before these capabilities existed have no entry for them. That is
      // safe: getCapabilityOptionForRole() falls back to this default map whenever the stored value
      // is missing, so existing roles resolve to NO without a data migration.
      [UserCapability.RATE_DRYINGS]: UserCapabilityOption.NO,
      [UserCapability.MANAGE_PRODUCTION]: UserCapabilityOption.NO,
      [UserCapability.MANAGE_CLIENTS]: UserCapabilityOption.NO,
    },
  };
}